import { useQuery, useMutation, type QueryFunction, type UseQueryOptions, type UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  role: string;
  addedAt: string;
  userName: string;
  userEmail: string;
}

export interface AddProjectMemberBody {
  userId: string;
  role?: string;
}

const getListProjectMembersUrl = (projectId: string) => `/api/projects/${projectId}/members`;

const listProjectMembers = async (projectId: string, options?: RequestInit): Promise<ProjectMember[]> =>
  customFetch<ProjectMember[]>(getListProjectMembersUrl(projectId), { ...options, method: "GET" });

export const getListProjectMembersQueryKey = (projectId: string) =>
  [`/api/projects/${projectId}/members`] as const;

export function useListProjectMembers<TData = ProjectMember[]>(
  projectId: string,
  options?: Omit<UseQueryOptions<ProjectMember[], unknown, TData>, "queryKey" | "queryFn">,
) {
  return useQuery<ProjectMember[], unknown, TData>({
    queryKey: getListProjectMembersQueryKey(projectId),
    queryFn: ({ signal }: { signal?: AbortSignal }) => listProjectMembers(projectId, { signal }),
    enabled: !!projectId,
    ...options,
  });
}

const addProjectMember = async (projectId: string, body: AddProjectMemberBody): Promise<ProjectMember> =>
  customFetch<ProjectMember>(getListProjectMembersUrl(projectId), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export function useAddProjectMember(
  options?: UseMutationOptions<ProjectMember, unknown, { projectId: string; data: AddProjectMemberBody }>,
) {
  return useMutation<ProjectMember, unknown, { projectId: string; data: AddProjectMemberBody }>({
    mutationFn: ({ projectId, data }) => addProjectMember(projectId, data),
    ...options,
  });
}

const updateProjectMemberRole = async (projectId: string, userId: string, role: string): Promise<ProjectMember> =>
  customFetch<ProjectMember>(`/api/projects/${projectId}/members/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });

export function useUpdateProjectMemberRole(
  options?: UseMutationOptions<ProjectMember, unknown, { projectId: string; userId: string; role: string }>,
) {
  return useMutation<ProjectMember, unknown, { projectId: string; userId: string; role: string }>({
    mutationFn: ({ projectId, userId, role }) => updateProjectMemberRole(projectId, userId, role),
    ...options,
  });
}

const removeProjectMember = async (projectId: string, userId: string): Promise<void> =>
  customFetch<void>(`/api/projects/${projectId}/members/${userId}`, { method: "DELETE" });

export function useRemoveProjectMember(
  options?: UseMutationOptions<void, unknown, { projectId: string; userId: string }>,
) {
  return useMutation<void, unknown, { projectId: string; userId: string }>({
    mutationFn: ({ projectId, userId }) => removeProjectMember(projectId, userId),
    ...options,
  });
}
